import { api } from '@/services/api/AxiosBaseService'

/** Cobrança existente no Asaas que ainda não tem fatura no sistema. */
export interface AsaasUnlinkedCharge {
  paymentId: string
  value: number
  dueDate: string | null
  paymentDate: string | null
  status: string | null
  billingType: string | null
  description: string | null
  invoiceUrl: string | null
  isPaid: boolean
}

/** Fatura local sem cobrança — candidata a receber o vínculo. */
export interface LinkableInvoice {
  id: string
  code: string
  amount: number
  dueDate: string | null
  paidDate: string | null
  status: string
}

export interface AsaasUnlinkedResult {
  charges: AsaasUnlinkedCharge[]
  linkableInvoices: LinkableInvoice[]
  /** Preenchido quando não há o que buscar (sem cadastro no Asaas etc.). */
  reason?: string
}

/** Linha do relatório de resincronização. */
export interface ResyncRow {
  code: string
  dueDate: string | null
  amount: number
  /** `asaas` = cobrança encontrada no gateway; `system` = fatura do sistema. */
  result: 'asaas' | 'system'
  paymentId: string | null
  /** Campos alterados nesta fatura (vazio = já estava correta). */
  changes: string[]
}

export interface ResyncResult {
  checked: number
  matched: number
  unmatched: number
  updated: number
  rows: ResyncRow[]
  /** Cobranças que existem no Asaas e não têm fatura no sistema. */
  orphanCharges: AsaasUnlinkedCharge[]
  /** Preenchido quando nada foi verificado (sem integração, cliente sem Asaas). */
  reason?: string | null
  recalculatedInvoices: number
}

/** Prévia da importação de extrato de outro mês. */
export interface StatementImportPreview {
  source: { code: string; dueDate: string | null; amount: number } | null
  items: Array<{ subscriberId: string | null; name: string; planAmount: number; discount: number; amount: number }>
  /** Quem estava no mês de origem mas não vale nesta competência. */
  dropped: Array<{ name: string; reason: string }>
  /** Quem valia nesta competência e não está no extrato de origem. */
  missing: Array<{ name: string; joinedAt: string | null }>
  total: number
  targetAmount: number
}

/** Assinante OU empresa — a busca é sempre de um cliente só. */
export type RevenueOwner = { subscriberId: string } | { companyId: string }

function ownerParams(owner: RevenueOwner): Record<string, string> {
  return 'subscriberId' in owner
    ? { subscriber_id: owner.subscriberId }
    : { company_id: owner.companyId }
}

/**
 * Manutenção financeira de faturas (administração): busca no Asaas, edição de
 * datas e exclusão. Todas as rotas exigem permissão específica no backend.
 */
type SyncStatus = 'PENDENTE' | 'PROCESSANDO' | 'CONCLUIDO' | 'FALHOU'

interface SyncRun<T> {
  id: string
  status: SyncStatus
  result: T | null
  error: string | null
}

/**
 * Acompanha uma consulta ao Asaas rodando em background.
 *
 * Consultar o gateway leva o tempo que ele quiser — cada chamada tenta até 5
 * vezes com espera crescente quando ele responde 429, o que sozinho passa de
 * dois minutos. Fazer isso dentro de uma requisição estourava o timeout de 30s
 * do navegador: a tela mostrava erro e perdia o resultado enquanto o servidor
 * continuava reconciliando faturas. Aqui cada consulta de status é instantânea.
 */
async function trackRun<T>(runId: string, timeoutMs = 10 * 60_000): Promise<T> {
  const startedAt = Date.now()
  for (;;) {
    const r = await api.get<{ data: SyncRun<T> }>(`/financial/revenues/asaas-sync/${runId}`)
    const d = r.data.data
    if (d.status === 'CONCLUIDO' && d.result) return d.result
    if (d.status === 'FALHOU') throw new Error(d.error || 'Falha ao consultar o Asaas.')
    if (Date.now() - startedAt > timeoutMs) {
      throw new Error('A consulta ao Asaas está demorando mais que o esperado. Tente de novo em alguns minutos.')
    }
    await new Promise((resolve) => setTimeout(resolve, 2000))
  }
}

export const RevenueMaintenanceService = {
  /** Cobranças do cliente no Asaas que não estão no sistema. */
  async asaasUnlinked(owner: RevenueOwner): Promise<AsaasUnlinkedResult> {
    const r = await api.get<{ data: { id: string } }>(
      '/financial/revenues/asaas-unlinked',
      { params: ownerParams(owner) },
    )
    const data = await trackRun<{
      charges: AsaasUnlinkedCharge[]
      linkableInvoices: LinkableInvoice[]
      reason?: string | null
      truncated?: boolean
    }>(r.data.data.id)
    return { ...data, reason: data.reason ?? undefined }
  },

  /**
   * Reconcilia todas as faturas do cliente com o Asaas: casa por id e, na falta
   * dele, por vencimento + valor. O que o Asaas não conhece vira "Sistema".
   */
  async resync(owner: RevenueOwner): Promise<ResyncResult> {
    const r = await api.post<{ data: { id: string } }>(
      '/financial/revenues/resync-asaas',
      ownerParams(owner),
    )
    return trackRun<ResyncResult>(r.data.data.id)
  },

  /** Cria uma fatura nova a partir da cobrança. */
  async importCharge(owner: RevenueOwner, paymentId: string): Promise<{ id: string; code: string }> {
    const r = await api.post<{ data: { id: string; code: string } }>(
      '/financial/revenues/asaas-unlinked/import',
      { payment_id: paymentId, ...ownerParams(owner) },
    )
    return r.data.data
  },

  /** Vincula a cobrança a uma fatura local existente (sobrescreve valor e datas). */
  async linkCharge(revenueId: string, paymentId: string): Promise<{ id: string; code: string }> {
    const r = await api.post<{ data: { id: string; code: string } }>(
      `/financial/revenues/${revenueId}/link-asaas`,
      { payment_id: paymentId },
    )
    return r.data.data
  },

  /** O que seria importado do mês informado — não grava nada. */
  async previewStatements(revenueId: string, month: number, year: number): Promise<StatementImportPreview> {
    const r = await api.get<{ data: StatementImportPreview }>(
      `/financial/revenues/${revenueId}/import-statements/preview`,
      { params: { month, year } },
    )
    return r.data.data
  },

  /** Copia o extrato do mês informado para a fatura. */
  async importStatements(revenueId: string, month: number, year: number): Promise<{ imported: number; dropped: number; total: number; recalculated: number }> {
    const r = await api.post<{ data: { imported: number; dropped: number; total: number; recalculated: number } }>(
      `/financial/revenues/${revenueId}/import-statements`,
      { month, year },
    )
    return r.data.data
  },

  /** Altera vencimento e pagamento. Retorna quantas faturas foram recalculadas. */
  async updateDates(revenueId: string, dueDate: string, paidDate: string | null): Promise<number> {
    const r = await api.put<{ meta: { recalculatedInvoices: number } }>(
      `/financial/revenues/${revenueId}/dates`,
      { due_date: dueDate, paid_date: paidDate || null },
    )
    return r.data.meta.recalculatedInvoices
  },

  /** Exclusão lógica. Retorna quantas faturas foram recalculadas. */
  async destroy(revenueId: string, reason: string): Promise<number> {
    const r = await api.delete<{ meta: { recalculatedInvoices: number } }>(
      `/financial/revenues/${revenueId}`,
      { data: { reason: reason || null } },
    )
    return r.data.meta.recalculatedInvoices
  },
}
